// Geboortevenster · Betrouwbaarheid: marges per eigenschap en per laag, kleurdrempels (15/30/60 min, kritiek < 5) en de indicator-lagen.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { nearestBirthWindowPlanetChange } from './birth-window-planets.builder';
import {
  HD_BIRTH_WINDOW_INDICATOR_TIERS,
  HD_BIRTH_WINDOW_TIER_OF,
  HdBirthWindowData,
  HdBirthWindowLane,
  HdBirthWindowPropertyKey,
  HdBirthWindowTier,
} from './birth-window.models';

/**
 * Marges en betrouwbaarheidskleuren (spec §3.1, besluit 2 van Steven 2026-10-16):
 * geboortetijden worden in de praktijk tot een kwartier afgerond of te laat
 * genoteerd, dus alles binnen een kwartier is onbetrouwbaar (rood), tot een
 * half uur gevoelig (oranje), tot een uur matig (geel), daarboven groen. Een
 * marge onder vijf minuten krijgt daarbovenop een "kritiek"-markering.
 */
export const HD_BIRTH_WINDOW_THRESHOLD_MIN = { red: 15, orange: 30, yellow: 60 } as const;
export const HD_BIRTH_WINDOW_CRITICAL_MIN = 5;

const MINUTE_MS = 60_000;

export type HdBirthWindowLevelColor = 'red' | 'orange' | 'yellow' | 'green';

export interface HdBirthWindowPropertyMargin {
  readonly key: HdBirthWindowPropertyKey;
  readonly tier: HdBirthWindowTier;
  /** Afstand (ms) tot de dichtstbijzijnde wijziging vóór de geboorte; undefined = geen binnen het venster. */
  readonly beforeMs?: number;
  /** Afstand (ms) tot de dichtstbijzijnde wijziging ná de geboorte; undefined = geen binnen het venster. */
  readonly afterMs?: number;
  readonly estimated: boolean;
}

export interface HdBirthWindowTierMargin {
  readonly tier: HdBirthWindowTier;
  /** Kleinste marge in de laag (ms); undefined = niets wijzigt binnen het venster. */
  readonly nearestMs?: number;
  /** Richting van de dichtstbijzijnde wijziging. */
  readonly direction?: 'before' | 'after';
  readonly color: HdBirthWindowLevelColor;
  readonly critical: boolean;
  /** Eigenschappen die op die kleinste marge wisselen (leeg voor de planetenlaag). */
  readonly drivers: readonly HdBirthWindowPropertyKey[];
  readonly estimated: boolean;
}

export function colorForMargin(ms: number | undefined): HdBirthWindowLevelColor {
  if (ms === undefined) return 'green';
  const minutes = ms / MINUTE_MS;
  if (minutes < HD_BIRTH_WINDOW_THRESHOLD_MIN.red) return 'red';
  if (minutes < HD_BIRTH_WINDOW_THRESHOLD_MIN.orange) return 'orange';
  if (minutes < HD_BIRTH_WINDOW_THRESHOLD_MIN.yellow) return 'yellow';
  return 'green';
}

export function isCriticalMargin(ms: number | undefined): boolean {
  return ms !== undefined && ms < HD_BIRTH_WINDOW_CRITICAL_MIN * MINUTE_MS;
}

/**
 * Per lane de afstand van de geboorte tot de rand van het geboorteblok, enkel
 * als er aan die kant nog een ander blok ligt (anders is er binnen het venster
 * geen wijziging).
 */
export function marginsFromLanes(
  lanes: readonly HdBirthWindowLane[],
  birthMs: number,
  estimated = false,
): HdBirthWindowPropertyMargin[] {
  return lanes.map(lane => {
    const index = lane.blocks.findIndex(block => block.state === 'birth');
    if (index < 0) return { key: lane.key, tier: lane.tier, estimated };
    const block = lane.blocks[index];
    const beforeMs = index > 0 ? birthMs - block.fromMs : undefined;
    const afterMs = index < lane.blocks.length - 1 ? block.toMs - birthMs : undefined;
    return { key: lane.key, tier: lane.tier, beforeMs, afterMs, estimated };
  });
}

function nearestOf(margin: { beforeMs?: number; afterMs?: number }): { ms: number; direction: 'before' | 'after' } | undefined {
  const candidates: { ms: number; direction: 'before' | 'after' }[] = [];
  if (margin.beforeMs !== undefined) candidates.push({ ms: margin.beforeMs, direction: 'before' });
  if (margin.afterMs !== undefined) candidates.push({ ms: margin.afterMs, direction: 'after' });
  if (candidates.length === 0) return undefined;
  return candidates.sort((a, b) => a.ms - b.ms)[0];
}

function tierMarginOf(
  tier: HdBirthWindowTier,
  nearest: { ms: number; direction: 'before' | 'after' } | undefined,
  drivers: readonly HdBirthWindowPropertyKey[],
  estimated: boolean,
): HdBirthWindowTierMargin {
  return {
    tier,
    nearestMs: nearest?.ms,
    direction: nearest?.direction,
    color: colorForMargin(nearest?.ms),
    critical: isCriticalMargin(nearest?.ms),
    drivers,
    estimated,
  };
}

/** Planetenlaag: de dichtstbijzijnde poort-/lijnwissel van eender welke planeet op eender welke kant. */
export function planetTierMargin(data: HdBirthWindowData, birthMs: number, estimated = false): HdBirthWindowTierMargin {
  return tierMarginOf('planets', nearestOf(nearestBirthWindowPlanetChange(data, birthMs)), [], estimated);
}

/**
 * Marges per laag voor de indicator: Basics en Variabelen uit de
 * eigenschapsmarges (Base telt niet mee), Planeten uit de segmenten.
 */
export function tierMargins(
  margins: readonly HdBirthWindowPropertyMargin[],
  planets?: HdBirthWindowTierMargin,
): HdBirthWindowTierMargin[] {
  return HD_BIRTH_WINDOW_INDICATOR_TIERS.map(tier => {
    if (tier === 'planets') {
      return planets ?? tierMarginOf('planets', undefined, [], false);
    }
    const own = margins.filter(margin => HD_BIRTH_WINDOW_TIER_OF[margin.key] === tier);
    let nearest: { ms: number; direction: 'before' | 'after' } | undefined;
    for (const margin of own) {
      const candidate = nearestOf(margin);
      if (candidate && (!nearest || candidate.ms < nearest.ms)) nearest = candidate;
    }
    const drivers = nearest
      ? own.filter(margin => {
        const candidate = nearestOf(margin);
        return candidate !== undefined && Math.abs(candidate.ms - nearest!.ms) < 1_000;
      }).map(margin => margin.key)
      : [];
    return tierMarginOf(tier, nearest, drivers, own.some(margin => margin.estimated));
  });
}
